import { queryOptions, skipToken, useQueries } from '@tanstack/react-query'
import {
  ProductFeatureWithRoundRobin,
  FairlyPrioritizedUsers,
  DistributionApiClientV1,
} from '@chilipiper/api-client/src/distribution'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { DistributionUsers } from '../types'

const publicDistributionQuery = ({
  distribution,
  tenantId,
  distributionId,
  productFeature,
}: {
  distribution: DistributionApiClientV1
  tenantId: string
  distributionId?: string
  productFeature?: ProductFeatureWithRoundRobin
}) =>
  queryOptions({
    queryKey: [
      'distribution/get-public-distribution',
      tenantId,
      distributionId,
      ...(productFeature ? [productFeature] : []),
    ],
    queryFn: distributionId
      ? () =>
          distribution
            .publicGetOrderedListOfUsers(tenantId, distributionId, {
              productFeature,
            })
            .then(result => ({
              ...result,
              id: distributionId,
            }))
      : skipToken,
  })

export const usePublicDistributions = (
  params: Omit<Parameters<typeof publicDistributionQuery>[0], 'distributionId' | 'distribution'> & {
    distributionIds: string[]
  }
) => {
  const { distribution } = useApiClient()
  return useQueries({
    queries: params.distributionIds.map(distributionId =>
      publicDistributionQuery({
        ...params,
        distributionId,
        distribution,
      })
    ),
    combine: results => {
      return results.reduce(
        (acc, result) => {
          if (result.data && result.isSuccess) {
            acc[result.data.id] = result.data
          }
          return acc
        },
        {} as Record<string, FairlyPrioritizedUsers>
      )
    },
  })
}

export const useDistributionsUserList = ({
  tenantId,
  distributionIds = [],
  productFeature,
}: {
  tenantId: string
  distributionIds?: string[]
  productFeature?: ProductFeatureWithRoundRobin
}): DistributionUsers[] => {
  const distributions = usePublicDistributions({
    distributionIds,
    tenantId,
    productFeature,
  })

  const data = Object.keys(distributions).map(id => ({
    id,
    ...distributions[id],
  }))

  return data
}
